import { useEffect, useRef } from 'react'

import { AD_KPI_KEYS, type AdKpi, type AdvertisingOverview } from '@/api/advertisingOverview'
import { deltaPresentation, LIKE_FOR_LIKE } from '@/components/dataQuality'
import { straddleAttributes } from '@/components/DeltaBadge'
import { Card, EmptyState, KpiCard, LoadingBlock } from '@/design'
import { dayCount, type Comparison } from '@/lib/filters'

import {
  ALL_SALES_CAVEAT,
  KPI_NULL_REASON_TEXT,
  KPI_POLARITY,
  NOT_AVAILABLE_TEXT,
  UNDEFINED_FIGURE,
  formatKpiValue,
  kpiLabel,
} from './definitions'
import { advertisingSectionError } from './errors'
import { FigureSourceBadge } from './FigureSourceBadge'
import type { OverviewQueryLike } from './overviewQuery'

/** What a tile says in place of a delta, or `undefined` to let the delta show. */
function comparisonNote(kpi: AdKpi, overview: AdvertisingOverview, compare: Comparison): string | undefined {
  const reason = overview.notAvailable[kpi.key]
  if (reason) return NOT_AVAILABLE_TEXT[reason].label
  if (kpi.current === null) return kpi.nullReason ? KPI_NULL_REASON_TEXT[kpi.nullReason] : 'Not available'
  if (compare === 'none') return 'No comparison'
  if (overview.meta.previousUnavailable) return 'No comparison: a previous-period currency rate is missing'
  if (kpi.previous === null) return 'No previous-period figure'
  if (kpi.deltaPct === null) return 'Previous period was 0'
  return undefined
}

function KpiTile({
  kpi,
  overview,
  compare,
  focused,
}: {
  kpi: AdKpi
  overview: AdvertisingOverview
  compare: Comparison
  focused: boolean
}) {
  const { meta } = overview
  const note = comparisonNote(kpi, overview, compare)
  // The API's delta is a percent; a tile's `change` is a fraction.
  const change = note === undefined && kpi.deltaPct !== null ? kpi.deltaPct / 100 : null
  const reason = overview.notAvailable[kpi.key]
  // A TmT figure's change across the tracking baseline is not like-for-like: shown neutral, the notice as its tooltip
  // and description (`straddleAttributes`, as every table's change). Meta's figures are Meta's own counts, which the
  // start of TmT tracking cannot skew. An unavailable figure has no change, so its own reason is the tile's tooltip.
  const comparison = kpi.source === 'tmt' && change !== null ? deltaPresentation(overview.dataQuality) : LIKE_FOR_LIKE
  const straddles = comparison.neutral
  const previous =
    kpi.previous !== null && compare !== 'none' && !meta.previousUnavailable
      ? `Previous ${formatKpiValue(kpi, kpi.previous, meta.currency)}`
      : undefined
  // Every sale over the spend is not attribution, and the tile says so wherever it is read.
  const detail = kpi.source === 'all_sales' ? [ALL_SALES_CAVEAT, previous].filter(Boolean).join(' · ') : previous
  return (
    <div
      {...(straddles ? straddleAttributes(comparison) : { title: reason ? NOT_AVAILABLE_TEXT[reason].why : undefined })}
      data-focused={focused || undefined}
      className="aov-kpi"
    >
      <KpiCard
        label={kpiLabel(kpi.key, meta.currency)}
        value={kpi.current === null ? UNDEFINED_FIGURE : formatKpiValue(kpi, kpi.current, meta.currency)}
        change={change}
        polarity={straddles ? 'neutral' : KPI_POLARITY[kpi.key]}
        comparisonLabel={`vs previous ${dayCount(meta.previousRange.from, meta.previousRange.to)} days`}
        note={note}
        detail={detail}
        badge={
          <FigureSourceBadge
            source={kpi.source}
            model={meta.model}
            lookbackDays={meta.lookbackDays}
          />
        }
      />
    </div>
  )
}

/**
 * The KPI row: each figure with its delta against the previous period of the same
 * length (the API's own comparison), and whose number it is — TmT-attributed or
 * reported by Meta. `focus` (the Overview's ROAS drill-down, `?metric=roas`) brings
 * that tile into view and focus once the figures land.
 */
export function KpiSection({ query, compare, focus }: { query: OverviewQueryLike; compare: Comparison; focus: string | null }) {
  const focusRef = useRef<HTMLLIElement>(null)
  // Once per visit: a later refetch (another model or filter) must not pull focus off the control just used.
  const focusedOnce = useRef(false)
  const ready = query.data !== undefined

  useEffect(() => {
    if (!ready || focusedOnce.current || !focusRef.current) return
    focusedOnce.current = true
    focusRef.current.scrollIntoView?.({ block: 'center' })
    focusRef.current.focus({ preventScroll: true })
  }, [ready, focus])

  if (query.isError && !query.data) {
    return (
      <Card>
        <EmptyState variant="outage" compact description={advertisingSectionError(query.error, 'The advertising KPIs could not be loaded.')} />
      </Card>
    )
  }

  return (
    <section aria-labelledby="aov-kpis-heading" className="aov-kpis-section">
      <h2 id="aov-kpis-heading" className="ci-visually-hidden">
        Advertising KPIs
      </h2>
      {compare === 'previous_year' && query.data ? (
        <p className="aov-note" data-testid="kpi-compare-note">
          Changes are against the previous {dayCount(query.data.meta.previousRange.from, query.data.meta.previousRange.to)} days:
          advertising has no same-period-last-year comparison.
        </p>
      ) : null}
      <ul className="aov-kpis" aria-busy={query.isPending || undefined}>
        {query.data
          ? query.data.kpis.map((kpi) => {
              const focused = focus === kpi.key
              return (
                <li
                  key={kpi.key}
                  data-kpi={kpi.key}
                  data-source={kpi.source}
                  ref={focused ? focusRef : undefined}
                  tabIndex={focused ? -1 : undefined}
                >
                  <KpiTile kpi={kpi} overview={query.data as AdvertisingOverview} compare={compare} focused={focused} />
                </li>
              )
            })
          : AD_KPI_KEYS.map((key) => (
              <li key={key}>
                <div className="ci-kpi">
                  <LoadingBlock rows={2} />
                </div>
              </li>
            ))}
      </ul>
    </section>
  )
}
